import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { I18nProvider } from "./lib/i18n";
import Shell from "./components/Shell";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import TimeCard from "./pages/TimeCard";
import Crews from "./pages/Crews";
import { Employees, JobCodes, EquipmentPage, Clients } from "./pages/Crud";
import Ranches from "./pages/Ranches";
import ScoutingHome from "./pages/scouting/ScoutingHome";
import VisitFlow from "./pages/scouting/VisitFlow";
import ManagerReview from "./pages/scouting/ManagerReview";
import ClientReport from "./pages/scouting/ClientReport";
import WorkList from "./pages/WorkList";
import SeasonTimeline from "./pages/scouting/SeasonTimeline";
import PestDashboard from "./pages/pest/PestDashboard";
import SprayPlanner from "./pages/pest/SprayPlanner";
import Materials from "./pages/pest/Materials";
import SprayOrders from "./pages/pest/SprayOrders";
import OrderBuilder from "./pages/pest/OrderBuilder";
import FieldSheet from "./pages/pest/FieldSheet";
import CropEstimates from "./pages/CropEstimates";
import Training from "./pages/Training";
import Timesheets from "./pages/Timesheets";
import Export from "./pages/Export";
import SignOff from "./pages/SignOff";
import Approvals from "./pages/Approvals";
import DataImport from "./pages/DataImport";
import MyTimecard from "./pages/MyTimecard";
import EquipmentReport from "./pages/EquipmentReport";
import BreakAudit from "./pages/BreakAudit";
import Users from "./pages/Users";
import Expenses from "./pages/Expenses";
import Mileage from "./pages/Mileage";
import ClientLogins from "./pages/ClientLogins";
import ClientPortal from "./pages/portal/ClientPortal";
import Budgets from "./pages/Budgets";
import IrrigationHome from "./pages/irrigation/IrrigationHome";
import IrrigatorLog from "./pages/irrigation/IrrigatorLog";
import ClientRequests from "./pages/ClientRequests";
import EstateHome from "./pages/estate/EstateHome";

function homeFor(role) {
  if (role === "tractor_driver") return "/my-timecard";
  if (role === "irrigator") return "/irrigation/log";
  if (role === "client") return "/portal";
  return "/";
}

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8F6F2]">
      <div className="font-medium tracking-wide uppercase">Loading…</div>
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={homeFor(user.role)} replace />;
  return <Shell>{children}</Shell>;
}

function App() {
  return (
    <BrowserRouter>
      <I18nProvider>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={<Protected roles={["admin", "supervisor", "foreman", "employee"]}><Dashboard /></Protected>} />
            <Route path="/my-timecard" element={<Protected roles={["tractor_driver", "irrigator", "admin", "supervisor"]}><MyTimecard /></Protected>} />
            <Route path="/equipment-report" element={<Protected roles={["admin", "supervisor"]}><EquipmentReport /></Protected>} />
            <Route path="/break-audit" element={<Protected roles={["admin", "supervisor"]}><BreakAudit /></Protected>} />
            <Route path="/timecard" element={<Protected roles={["admin", "supervisor", "foreman"]}><TimeCard /></Protected>} />
            <Route path="/crews" element={<Protected roles={["admin", "supervisor", "foreman"]}><Crews /></Protected>} />
            <Route path="/signoff" element={<Protected><SignOff /></Protected>} />
            <Route path="/employees" element={<Protected roles={["admin", "supervisor"]}><Employees /></Protected>} />
            <Route path="/jobcodes" element={<Protected roles={["admin", "supervisor"]}><JobCodes /></Protected>} />
            <Route path="/equipment" element={<Protected roles={["admin", "supervisor"]}><EquipmentPage /></Protected>} />
            <Route path="/ranches" element={<Protected roles={["admin", "supervisor"]}><Ranches /></Protected>} />
            <Route path="/clients" element={<Protected roles={["admin", "supervisor"]}><Clients /></Protected>} />
            <Route path="/client-logins" element={<Protected roles={["admin", "supervisor"]}><ClientLogins /></Protected>} />
            <Route path="/client-requests" element={<Protected roles={["admin", "supervisor"]}><ClientRequests /></Protected>} />
            <Route path="/estate" element={<Protected roles={["admin", "supervisor", "foreman"]}><EstateHome /></Protected>} />
            <Route path="/budgets" element={<Protected roles={["admin"]}><Budgets /></Protected>} />
            <Route path="/irrigation" element={<Protected roles={["admin", "supervisor"]}><IrrigationHome /></Protected>} />
            <Route path="/irrigation/log" element={<Protected roles={["admin", "supervisor", "irrigator"]}><IrrigatorLog /></Protected>} />
            <Route path="/portal" element={<Protected roles={["client"]}><ClientPortal /></Protected>} />
            <Route path="/portal/requests" element={<Protected roles={["client"]}><ClientPortal initialTab="requests" /></Protected>} />
            <Route path="/scouting" element={<Protected roles={["admin", "supervisor"]}><ScoutingHome /></Protected>} />
            <Route path="/scouting/visit/:id" element={<Protected roles={["admin", "supervisor"]}><VisitFlow /></Protected>} />
            <Route path="/scouting/review/:id" element={<Protected roles={["admin", "supervisor"]}><ManagerReview /></Protected>} />
            <Route path="/scouting/report/:id" element={<Protected roles={["admin", "supervisor"]}><ClientReport /></Protected>} />
            <Route path="/scouting/timeline/:ranchId" element={<Protected roles={["admin", "supervisor"]}><SeasonTimeline /></Protected>} />
            <Route path="/pest" element={<Protected roles={["admin", "supervisor"]}><PestDashboard /></Protected>} />
            <Route path="/pest/planner" element={<Protected roles={["admin", "supervisor"]}><SprayPlanner /></Protected>} />
            <Route path="/pest/materials" element={<Protected roles={["admin", "supervisor"]}><Materials /></Protected>} />
            <Route path="/pest/orders" element={<Protected roles={["admin", "supervisor", "tractor_driver"]}><SprayOrders /></Protected>} />
            <Route path="/pest/orders/new" element={<Protected roles={["admin", "supervisor"]}><OrderBuilder /></Protected>} />
            <Route path="/pest/orders/:id" element={<Protected roles={["admin", "supervisor", "tractor_driver"]}><FieldSheet /></Protected>} />
            <Route path="/crop" element={<Protected roles={["admin", "supervisor"]}><CropEstimates /></Protected>} />
            <Route path="/training" element={<Protected roles={["admin", "supervisor", "foreman"]}><Training /></Protected>} />
            <Route path="/worklist" element={<Protected roles={["admin", "supervisor", "foreman", "irrigator"]}><WorkList /></Protected>} />
            <Route path="/import" element={<Protected roles={["admin"]}><DataImport /></Protected>} />
            <Route path="/users" element={<Protected roles={["admin"]}><Users /></Protected>} />
            <Route path="/expenses" element={<Protected roles={["admin", "supervisor", "foreman", "tractor_driver", "irrigator"]}><Expenses /></Protected>} />
            <Route path="/mileage" element={<Protected roles={["admin", "supervisor", "foreman", "tractor_driver", "irrigator"]}><Mileage /></Protected>} />
            <Route path="/timesheets" element={<Protected roles={["admin", "supervisor", "foreman"]}><Timesheets /></Protected>} />
            <Route path="/approvals" element={<Protected roles={["admin", "supervisor", "foreman"]}><Approvals /></Protected>} />
            <Route path="/export" element={<Protected roles={["admin", "supervisor"]}><Export /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </I18nProvider>
    </BrowserRouter>
  );
}

export default App;
